// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// CareRx Plan of Treatment (the OwlRx form + the D-POT-7 worklist) — TanStack Query hooks (E-02:
// components never call api.* directly). The worklist reads (one page per view + the summary) and the
// row actions, each invalidating every POT read on success.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  claimCareRxPot, duplicateCareRxPot, listCareRxFormulary, listCareRxNurses, listCareRxPotEligible, listPatients, matchCareRxDrug, refreshCareRxEsign, startCareRxPot, updateCareRxStaffing, voidCareRxEsign,
} from '../../api/client'
import { getAppVersion, getNativePotHandoff, getPotWorklist, getPotWorklistSummary, nativePotHandoffKey, requestDrugAdd, type PotWorklistParams } from '../../api/carerxPot'
import { reassignCareRxPot, shareCareRxPot, voidPotVisit, type PotClaimResult } from '../../pages/pot/potApi'

export const carerxPotKeys = {
  all: ['carerx', 'pot'] as const,
  eligible: ['carerx', 'pot', 'eligible'] as const,
  worklist: (p: PotWorklistParams) => ['carerx', 'pot', 'worklist', p] as const,
  worklistSummary: ['carerx', 'pot', 'worklist-summary'] as const,
  patientSearch: (q: string) => ['carerx', 'pot', 'patient-search', q] as const,
  nurses: ['carerx', 'nurses', 'roster'] as const,
  formularyActive: ['carerx', 'formulary', 'active'] as const,
  drugMatch: (name: string) => ['carerx', 'drug-matrix', 'match', name] as const,
  version: ['app', 'version'] as const,
}

// ── Plan of Treatment form (D-POT-6 rows 4, 21, 36–39) ───────────────────────────────────────

/** asp-pot loadDrugMatrixOnce: the active controlled drug list, loaded once and filtered client-side. */
export function useActiveFormulary(enabled: boolean) {
  return useQuery({ queryKey: carerxPotKeys.formularyActive, queryFn: () => listCareRxFormulary({ active: true }), enabled, staleTime: 300_000, select: r => r.items ?? [] })
}
/** The clinical reference matrix entry (black-box warnings, DDIs, monitoring) for a drug name. */
export function useDrugMatrixMatch(name: string) {
  return useQuery({ queryKey: carerxPotKeys.drugMatch(name), queryFn: () => matchCareRxDrug(name), enabled: name.trim().length >= 2, staleTime: 300_000, select: r => r.match })
}
/** asp-pot requestDrugAdd — the "Drug not listed? Request" action. */
export const useRequestDrugAdd = () => useMutation({ mutationFn: (body: { drug_name: string; notes?: string }) => requestDrugAdd(body) })
/** The pharmacist worksheet hand-off for a visit's therapy — read ONCE at load (no focus refetch:
 *  a late-arriving hand-off must never overwrite what the nurse has typed since). */
export function useNativePotHandoff(visitId: string, enabled: boolean) {
  return useQuery({ queryKey: nativePotHandoffKey(visitId), queryFn: () => getNativePotHandoff(visitId), enabled, staleTime: Infinity, retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false })
}
/** asp-pot version badge — GET /version. */
export const useAppVersion = () => useQuery({ queryKey: carerxPotKeys.version, queryFn: getAppVersion, staleTime: Infinity, retry: false })

/** D-POT-7: one server page of a worklist view; the previous page stays on screen while the next loads. */
export function usePotWorklist(p: PotWorklistParams, enabled = true) {
  return useQuery({ queryKey: carerxPotKeys.worklist(p), queryFn: () => getPotWorklist(p), placeholderData: keepPreviousData, staleTime: 15_000, enabled })
}
/** D-POT-7: the tile counts, avg minutes, the void right and the filter facets. */
export function usePotWorklistSummary() {
  return useQuery({ queryKey: carerxPotKeys.worklistSummary, queryFn: getPotWorklistSummary, staleTime: 15_000 })
}

/** Start a POT by hand: patients matching the typed name / MRN (2+ characters). */
export function usePotPatientSearch(q: string) {
  const t = q.trim()
  return useQuery({ queryKey: carerxPotKeys.patientSearch(t), queryFn: () => listPatients(t, 8), enabled: t.length >= 2, staleTime: 30_000, select: r => r.items ?? [] })
}

/** Patients cleared to start a POT (enrollment approved + consent signed). Fetched only while asked for. */
export function usePotEligible(enabled: boolean) {
  return useQuery({ queryKey: carerxPotKeys.eligible, queryFn: listCareRxPotEligible, enabled, select: r => r.items ?? [] })
}

/** The nurse roster for the Reassign… / Share… pickers; fetched the first time a picker opens. */
export function useCareRxNurseRoster(enabled: boolean) {
  return useQuery({ queryKey: carerxPotKeys.nurses, queryFn: () => listCareRxNurses(), enabled, staleTime: 300_000, select: r => r.items ?? [] })
}

/** Every POT write goes stale-and-refetch on the one dashboard read (and the eligible list, which a start consumes). */
function usePotMutation<TVars, TOut>(mutationFn: (v: TVars) => Promise<TOut>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn, onSuccess: () => qc.invalidateQueries({ queryKey: carerxPotKeys.all }) })
}

export const useClaimPot = () => usePotMutation(async (visitId: string): Promise<PotClaimResult> => claimCareRxPot(visitId))
export const useVoidPotVisit = () => usePotMutation((v: { visitId: string; reason: string }) => voidPotVisit(v.visitId, v.reason))
export const useReassignPot = () => usePotMutation((v: { visitId: string; nurseId: string; reason: string }) => reassignCareRxPot(v.visitId, { nurse_id: v.nurseId, reason: v.reason }))
export const useSharePot = () => usePotMutation((v: { visitId: string; accountId: string; reason?: string }) => shareCareRxPot(v.visitId, { account_id: v.accountId, reason: v.reason }))
export const useDuplicatePot = () => usePotMutation((visitId: string) => duplicateCareRxPot(visitId))
export const useRevokePotEnvelope = () => usePotMutation((v: { envelopeId: string; reason: string }) => voidCareRxEsign(v.envelopeId, v.reason))
export const useRefreshPotEnvelope = () => usePotMutation((envelopeId: string) => refreshCareRxEsign(envelopeId))
export const useStartPot = () => usePotMutation((v: { therapyId: string; visitDate: string }) => startCareRxPot(v.therapyId, { visit_date: v.visitDate }))
/** Sets the date of an unscheduled visit (mig 334: delivery-created visits are born without one). */
export const useSchedulePotVisit = () => usePotMutation((v: { visitId: string; visitDate: string }) => updateCareRxStaffing(v.visitId, { action: 'schedule', visit_date: v.visitDate }))
